import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import {
  AnimationMixer,
  Box3,
  Group,
  Mesh,
  SRGBColorSpace,
  Vector3,
  type AnimationAction,
  type AnimationClip,
  type Material,
  type MeshStandardMaterial,
} from "three";
import { useState } from "react";
import { AvatarController, type AvatarState } from "../animation/AvatarController";
import { useJourneyStore } from "../state/journeyMachine";
import { createGltfLoader } from "../loaders/loaders";
import type { Waypoint } from "../scenes/sede/waypoints";

const AVATAR_URL = "/assets/models/avatar.glb";
const CLIP_URLS: Record<Exclude<AvatarState, "idle"> | "idle", string> = {
  idle: "/assets/models/anim_idle.glb",
  walk: "/assets/models/anim_walk.glb",
  sit: "/assets/models/anim_sit.glb",
  observe: "/assets/models/anim_observe.glb",
  turn: "/assets/models/anim_turn.glb",
};

interface AvatarAssets {
  scene: Group;
  clips: Record<AvatarState, AnimationClip>;
  /** Escala correctiva para que el modelo mida ~1.72 m (GLB en cm/unidades raras). */
  scale: number;
}

/**
 * Carga perezosa del avatar y sus 5 GLB de animación (mismo esqueleto):
 * extrae el primer AnimationClip de cada archivo y lo renombra a
 * idle/walk/sit/observe/turn. Si algo falla, devuelve null y el
 * componente cae al placeholder humanoide.
 */
function useAvatarAssets(): AvatarAssets | null {
  const [assets, setAssets] = useState<AvatarAssets | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loader = createGltfLoader();
    const load = (url: string) =>
      new Promise<import("three-stdlib").GLTF>((resolve, reject) =>
        loader.load(url, resolve, undefined, reject),
      );

    (async () => {
      try {
        const avatar = await load(AVATAR_URL);
        const entries = await Promise.all(
          (Object.keys(CLIP_URLS) as AvatarState[]).map(async (state) => {
            const gltf = await load(CLIP_URLS[state]);
            const clip = gltf.animations[0];
            if (!clip) throw new Error(`sin clip en ${CLIP_URLS[state]}`);
            // Los clips traen tracks de escala del armature (export Mixamo):
            // encogen el modelo a centímetros. Se eliminan; basta posición+rotación.
            clip.tracks = clip.tracks.filter((t) => !t.name.endsWith(".scale"));
            clip.name = state;
            return [state, clip] as const;
          }),
        );
        if (cancelled) return;
        fixAvatarMaterials(avatar.scene);
        // Normaliza la escala: mide la altura en bind pose y corrige a ~1.72 m
        const bbox = new Box3().setFromObject(avatar.scene);
        const height = bbox.getSize(new Vector3()).y;
        const scale = height > 0.01 && (height < 1.2 || height > 2.6) ? 1.72 / height : 1;
        setAssets({
          scene: avatar.scene,
          clips: Object.fromEntries(entries) as Record<AvatarState, AnimationClip>,
          scale,
        });
      } catch {
        if (!cancelled) setAssets(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return assets;
}

/**
 * Corrige materiales que renderizan como silueta negra: metalness alto sin
 * envMap, texturas sin sRGB o falta de luz ambiental. Se asegura
 * baseColorTexture en sRGB, metalness acotado y envMapIntensity razonable.
 */
function fixAvatarMaterials(root: Group): void {
  root.traverse((obj) => {
    if (!(obj as Mesh).isMesh) return;
    const mesh = obj as Mesh;
    mesh.castShadow = true;
    mesh.receiveShadow = false;
    // skinned mesh: su bounding box no refleja la pose real → nunca descartar por frustum
    mesh.frustumCulled = false;
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const mat of materials as Material[]) {
      const std = mat as MeshStandardMaterial;
      if (std.map) std.map.colorSpace = SRGBColorSpace;
      if (typeof std.metalness === "number") std.metalness = Math.min(std.metalness, 0.35);
      if (typeof std.roughness === "number") std.roughness = Math.max(std.roughness, 0.45);
      if ("envMapIntensity" in std) std.envMapIntensity = 1.1;
      std.needsUpdate = true;
    }
  });
}

interface AvatarProps {
  waypoint?: Waypoint;
}

const CROSSFADE = 0.3;

/**
 * Avatar del aspirante (1.75 m, GLB real texturizado con rig humanoide).
 * Reproduce idle/walk/sit/observe/turn con crossfade de 0.3 s según la
 * máquina de estados del AvatarController: idle por defecto, walk durante
 * goTo, sit al llegar a puesto teórico/cubículo, observe en preoperacional
 * y pistas, turn para giros > 120°. En modo guiado camina al waypoint de
 * cada etapa. Si el GLB no carga, placeholder humanoide proporcionado.
 */
export function Avatar({ waypoint }: AvatarProps) {
  const assets = useAvatarAssets();
  const guided = useJourneyStore((s) => s.guided);
  const reducedMotion = useJourneyStore((s) => s.reducedMotion);

  const controller = useMemo(() => new AvatarController([-18, 0, 26]), []);
  const groupRef = useRef<Group>(null);
  const mixerRef = useRef<AnimationMixer | null>(null);
  const actionsRef = useRef<Partial<Record<AvatarState, AnimationAction>>>({});
  const currentState = useRef<AvatarState>("idle");

  // cablear los clips al esqueleto del avatar
  useEffect(() => {
    if (!assets) return;
    const mixer = new AnimationMixer(assets.scene);
    const actions: Partial<Record<AvatarState, AnimationAction>> = {};
    for (const [state, clip] of Object.entries(assets.clips) as [AvatarState, AnimationClip][]) {
      actions[state] = mixer.clipAction(clip);
    }
    actions.idle?.play();
    mixerRef.current = mixer;
    actionsRef.current = actions;
    return () => {
      mixer.stopAllAction();
      mixerRef.current = null;
      actionsRef.current = {};
    };
  }, [assets]);

  // en modo guiado, cada cambio de etapa envía al avatar al waypoint caminando;
  // en modo libre se ubica de inmediato (el avatar siempre está presente)
  useEffect(() => {
    if (!waypoint) return;
    if (guided && !reducedMotion) {
      controller.goTo(waypoint.avatar);
      return;
    }
    controller.position.set(waypoint.avatar[0], 0, waypoint.avatar[2]);
    if (waypoint.avatarYaw !== undefined) controller.yaw = waypoint.avatarYaw;
    if (waypoint.avatarPose === "sit") controller.sit();
    else if (waypoint.avatarPose === "observe") controller.observe();
    else controller.stand();
  }, [waypoint, guided, reducedMotion, controller]);

  useFrame((_, delta) => {
    const group = groupRef.current;
    if (!group) return;
    controller.update(delta);
    // al llegar al punto de una etapa con pose, adoptarla (y mirar al frente del puesto)
    if (!controller.currentPose && waypoint?.avatarPose && controller.state === "idle") {
      if (waypoint.avatarPose === "sit") controller.sit();
      else controller.observe();
      if (waypoint.avatarYaw !== undefined) controller.yaw = waypoint.avatarYaw;
    }
    group.position.copy(controller.position);
    group.rotation.y = controller.yaw;

    // crossfade de 0.3 s según la máquina de estados
    const actions = actionsRef.current;
    const desired = controller.state;
    if (actions[desired] && desired !== currentState.current) {
      const from = actions[currentState.current];
      const to = actions[desired];
      if (from && to) {
        to.reset().fadeIn(CROSSFADE).play();
        from.fadeOut(CROSSFADE);
        currentState.current = desired;
      }
    }
    mixerRef.current?.update(delta);
  });

  return (
    <group ref={groupRef} name="avatar">
      {/* luz de relleno suave para que el modelo nunca salga negro */}
      <pointLight position={[0.6, 2.4, 0.8]} intensity={2.2} distance={5} decay={2} color="#fff4e4" />
      {assets ? (
        <group scale={assets.scale}>
          {/* una sola instancia: primitive monta la escena real que anima el mixer */}
          <primitive object={assets.scene} />
        </group>
      ) : (
        <HumanoidPlaceholder seated={waypoint?.avatarPose === "sit"} />
      )}
    </group>
  );
}

/**
 * Placeholder humanoide proporcionado (1.75 m de pie): pelvis, torso,
 * cabeza, brazos y piernas con materiales decentes. Sentado: piernas al frente.
 */
function HumanoidPlaceholder({ seated }: { seated: boolean }) {
  const skin = "#c9a486";
  const shirt = "#33555a";
  const pants = "#2e3a40";
  const legH = seated ? 0.45 : 0.85;
  const hipY = legH;
  const torsoY = hipY + 0.3;
  return (
    <group>
      {[-0.11, 0.11].map((x) => (
        <mesh key={x} position={[x, legH / 2, seated ? 0.18 : 0]} rotation={[seated ? -Math.PI / 2 : 0, 0, 0]} castShadow>
          <cylinderGeometry args={[0.07, 0.06, legH, 8]} />
          <meshStandardMaterial color={pants} roughness={0.8} />
        </mesh>
      ))}
      <mesh position={[0, hipY + 0.08, 0]} castShadow>
        <boxGeometry args={[0.34, 0.18, 0.22]} />
        <meshStandardMaterial color={pants} roughness={0.8} />
      </mesh>
      <mesh position={[0, torsoY + 0.26, 0]} castShadow>
        <boxGeometry args={[0.4, 0.52, 0.24]} />
        <meshStandardMaterial color={shirt} roughness={0.75} />
      </mesh>
      {[-0.26, 0.26].map((x) => (
        <mesh key={x} position={[x, torsoY + 0.22, seated ? 0.12 : 0]} rotation={[seated ? -0.7 : 0.08, 0, 0]} castShadow>
          <cylinderGeometry args={[0.05, 0.045, 0.58, 8]} />
          <meshStandardMaterial color={shirt} roughness={0.75} />
        </mesh>
      ))}
      <mesh position={[0, torsoY + 0.66, 0]} castShadow>
        <sphereGeometry args={[0.13, 16, 12]} />
        <meshStandardMaterial color={skin} roughness={0.6} />
      </mesh>
      <mesh position={[0, torsoY + 0.72, -0.02]} castShadow>
        <sphereGeometry args={[0.125, 16, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color="#2a2320" roughness={0.9} />
      </mesh>
    </group>
  );
}
